import { css } from '@emotion/react';
import { semantic_colors, text_styles, shapes, Typography } from '../../foundation';
import { buildCSSWithLength } from '../../styles';
import { InputSize, InputStatus, InputStepperSize } from './types';

export const input_status_color_css: Record<InputStatus, string> = {
  success: semantic_colors.state.positive,
  error: semantic_colors.state.negative,
  info: semantic_colors.content.secondary,
};

export const input_wrapper_base_css = css`
  ${text_styles.Body_13_Regular};
  max-width: 100%;
  width: 100%;
  display: inline-flex;
  column-gap: 8px;
  padding: 10px 16px;
  transition: border-color 0.1s linear;
  align-items: center;
  ${shapes.border_radius.small};

  &:hover,
  &:focus,
  &:focus-within {
    border-color: ${semantic_colors.border.active};
  }

  &:focus {
    outline: none;
  }
`;

export const input_base_css = css`
  font: inherit;
  color: inherit;
  margin: 0;
  width: 100%;
  padding: 0;
  border: none;
  border-radius: 0;
  outline: none;
  background: none;
  box-shadow: none;

  &::placeholder {
    color: ${semantic_colors.content.tertiary};
  }
`;

export const input_status_icon_css = css`
  display: flex;
  flex: none;
  align-items: center;
`;

const INPUT_STATUS_ICON_SIZE_BY_SIZE: Record<InputSize, number> = {
  xsmall: 14,
  small: 16,
  medium: 14,
  large: 14,
};

export const getInputStatusIconSize = (size: InputSize) => {
  return INPUT_STATUS_ICON_SIZE_BY_SIZE[size];
};

export const input_default_style = {
  backgroundColor: semantic_colors.background.surface,
  border: `1px solid ${semantic_colors.border.secondary}`,
  color: semantic_colors.content.primary,
};

export const input_error_style = {
  'border': `1px solid ${input_status_color_css.error}`,
  '&:focus, &:hover, &:focus-within': {
    border: `1px solid ${input_status_color_css.error}`,
  },
};

export const input_focus_style = {
  'outline': `3px solid ${semantic_colors.tab_focus}`,
  'zIndex': 1,
  '&:focus': {
    outline: `3px solid ${semantic_colors.tab_focus}`,
  },
};

export const input_disabled_style = {
  'backgroundColor': semantic_colors.background.disabled,
  'border': `1px solid ${semantic_colors.border.disabled}`,
  '-webkit-text-fill-color': semantic_colors.content.disabled,
  'color': semantic_colors.content.disabled,
  'opacity': 1,
  '&:focus, &:hover, &:focus-within': {
    border: `1px solid ${semantic_colors.border.disabled}`,
  },
};

export const input_entered_disabled_style = {
  'color': semantic_colors.content.secondary,
  '-webkit-text-fill-color': semantic_colors.content.secondary,
};

const INPUT_VERTICAL_PADDING_BY_SIZE: Record<InputSize, number> = { xsmall: 3, small: 7, medium: 8, large: 10 };
const INPUT_HORIZONTAL_PADDING_BY_SIZE: Record<InputSize, number> = { xsmall: 8, small: 10, medium: 12, large: 16 };

/** 필드 테두리와 컨텐츠 사이의 좌우 여백. InputGroup이 필드 가장자리 칸의 여백으로 재사용한다. */
export const getInputHorizontalPaddingBySize = (size: InputSize) => {
  return INPUT_HORIZONTAL_PADDING_BY_SIZE[size];
};

export const getInputPaddingBySize = (size: InputSize) => {
  return `${INPUT_VERTICAL_PADDING_BY_SIZE[size]}px ${INPUT_HORIZONTAL_PADDING_BY_SIZE[size]}px`;
};

export const getInputSpacingBySize = (size: InputSize) => {
  const spacing: { [key in InputSize]: number } = {
    xsmall: 4,
    small: 6,
    medium: 8,
    large: 8,
  };
  return spacing[size];
};
export type CommonSize = 'xsmall' | 'small' | 'medium' | 'large';
export const HEIGHT_BY_SIZE: Record<CommonSize, number> = { xsmall: 28, small: 32, medium: 38, large: 46 };
export const getInputHeightBySize = (size: CommonSize) => {
  const height: { [key in CommonSize]: number } = HEIGHT_BY_SIZE;
  return height[size];
};

export const getInputSizeStyle = (size: InputSize = 'large') => {
  return css`
    ${buildCSSWithLength('height', getInputHeightBySize(size))}
    ${buildCSSWithLength('column-gap', getInputSpacingBySize(size))}
    ${buildCSSWithLength('padding', getInputPaddingBySize(size))}
  `;
};

const INPUT_FONT_BY_SIZE: Record<InputSize, Typography> = {
  xsmall: 'Body_13_Regular',
  small: 'Body_13_Regular',
  medium: 'Body_13_Regular',
  large: 'Body_14_Regular',
};

export const getInputFontStyle = (size: InputSize) => {
  return css`
    ${text_styles[INPUT_FONT_BY_SIZE[size]]}
  `;
};

/**
 * input stepper
 * @MEMO: InputStepper만 small 사이즈가 존재
 */
const INPUT_STEPPER_HEIGHT_BY_SIZE: Record<InputStepperSize, number> = {
  xsmall: HEIGHT_BY_SIZE['xsmall'],
  small: HEIGHT_BY_SIZE['small'],
  medium: HEIGHT_BY_SIZE['medium'],
  large: HEIGHT_BY_SIZE['large'],
};

export const getInputSizeByInputStepperSize = (size: InputStepperSize): InputSize => {
  const input_stepper_size: Record<InputStepperSize, InputSize> = {
    xsmall: 'xsmall',
    small: 'xsmall',
    medium: 'medium',
    large: 'large',
  };
  return input_stepper_size[size];
};

export const getInputStepperHeightBySize = (size: InputStepperSize) => {
  return INPUT_STEPPER_HEIGHT_BY_SIZE[size];
};

export const getInputStepperButtonIconSize = (size: InputStepperSize) => {
  const input_stepper_button_icon_size: Record<InputStepperSize, number> = {
    xsmall: 12,
    small: 14,
    medium: 16,
    large: 24,
  };
  return input_stepper_button_icon_size[size];
};

export const getInputStepperButtonSizeStyle = (size: InputStepperSize = 'medium') => {
  const height = getInputStepperHeightBySize(size);
  return css`
    ${buildCSSWithLength('height', height)}
    ${buildCSSWithLength('width', height)}
    ${buildCSSWithLength('padding', (height - 2 - getInputStepperButtonIconSize(size)) / 2)}
  `;
};
